/**
 * Water-quality mode of the Water Chemistry tool (study-chemistry-tools-prd.md §8.3).
 *
 * Learners compare illustrative water samples with Australian Drinking Water Guidelines (ADWG)
 * values, calculate total hardness from calcium and magnesium, and read conductivity and dissolved
 * oxygen as context. Samples are teaching cases, not results from a real supply.
 */
import { checkNumericAnswer, formatSig, type AnswerCheck } from './gasLaws';

export type QualityParameter = 'ph' | 'tds' | 'hardness' | 'fluoride' | 'nitrate' | 'lead';
export type GuidelineKind = 'aesthetic' | 'health';

export interface QualityGuideline {
  parameter: QualityParameter;
  label: string;
  unit: string;
  kind: GuidelineKind;
  /** Lower limit, only for pH. */
  min?: number;
  max: number;
  reason: string;
}

export const QUALITY_SOURCES = [
  {
    label: 'NHMRC & NRMMC Australian Drinking Water Guidelines (ADWG)',
    detail:
      'Aesthetic values: pH 6.5–8.5, total dissolved solids 600 mg/L, total hardness 200 mg/L as CaCO₃. Health values: fluoride 1.5 mg/L, nitrate 50 mg/L (as nitrate, protects bottle-fed infants under 3 months), lead 0.01 mg/L.',
  },
  {
    label: 'IUPAC standard atomic weights',
    detail: 'Ca 40.08, Mg 24.31 and CaCO₃ 100.09 g mol⁻¹ for the hardness conversion.',
  },
  {
    label: 'USGS water-hardness classes and dissolved-oxygen solubility tables',
    detail:
      'Soft 0–60, moderately hard 61–120, hard 121–180, very hard over 180 mg/L as CaCO₃. Oxygen solubility in fresh water at 101.325 kPa.',
  },
] as const;

export const QUALITY_GUIDELINES: Record<QualityParameter, QualityGuideline> = {
  ph: { parameter: 'ph', label: 'pH', unit: '', kind: 'aesthetic', min: 6.5, max: 8.5, reason: 'Below 6.5 water can corrode pipes and fittings; above 8.5 it can taste bitter and form scale.' },
  tds: { parameter: 'tds', label: 'Total dissolved solids', unit: 'mg/L', kind: 'aesthetic', max: 600, reason: 'Above about 600 mg/L the dissolved salts start to make water taste noticeably salty or unpleasant.' },
  hardness: { parameter: 'hardness', label: 'Total hardness (as CaCO₃)', unit: 'mg/L', kind: 'aesthetic', max: 200, reason: 'Above 200 mg/L, Ca²⁺ and Mg²⁺ form scale in kettles and pipes and stop soap lathering.' },
  fluoride: { parameter: 'fluoride', label: 'Fluoride', unit: 'mg/L', kind: 'health', max: 1.5, reason: 'Above 1.5 mg/L, long-term intake can cause dental fluorosis (mottled teeth).' },
  nitrate: { parameter: 'nitrate', label: 'Nitrate (as NO₃⁻)', unit: 'mg/L', kind: 'health', max: 50, reason: 'Above 50 mg/L nitrate can cause methaemoglobinaemia (“blue baby” syndrome) in bottle-fed infants under 3 months.' },
  lead: { parameter: 'lead', label: 'Lead', unit: 'mg/L', kind: 'health', max: 0.01, reason: 'Lead is toxic, especially to the developing nervous system of children; there is no known safe level of exposure.' },
};

export const QUALITY_PARAMETER_ORDER: QualityParameter[] = ['ph', 'tds', 'hardness', 'fluoride', 'nitrate', 'lead'];

export const MOLAR_MASS_CA = 40.08;
export const MOLAR_MASS_MG = 24.31;
export const MOLAR_MASS_CACO3 = 100.09;
export const CA_TO_CACO3 = MOLAR_MASS_CACO3 / MOLAR_MASS_CA;
export const MG_TO_CACO3 = MOLAR_MASS_CACO3 / MOLAR_MASS_MG;

/** Total hardness, mg/L as CaCO₃, from Ca²⁺ and Mg²⁺ in mg/L. */
export function hardnessAsCaCO3(calciumMgL: number, magnesiumMgL: number): number {
  if (calciumMgL < 0 || magnesiumMgL < 0) return Number.NaN;
  return calciumMgL * CA_TO_CACO3 + magnesiumMgL * MG_TO_CACO3;
}

export type HardnessClass = 'soft' | 'moderately hard' | 'hard' | 'very hard';

const HARDNESS_CLASS_LIMITS: { max: number; label: HardnessClass }[] = [
  { max: 60, label: 'soft' },
  { max: 120, label: 'moderately hard' },
  { max: 180, label: 'hard' },
  { max: Number.POSITIVE_INFINITY, label: 'very hard' },
];

export function hardnessClass(hardness: number): HardnessClass | null {
  if (!Number.isFinite(hardness) || hardness < 0) return null;
  return (HARDNESS_CLASS_LIMITS.find((entry) => hardness <= entry.max) ?? HARDNESS_CLASS_LIMITS[3]).label;
}

/** Oxygen solubility in fresh water at 101.325 kPa, mg/L (USGS / Benson & Krause). */
export const OXYGEN_SATURATION_MG_L: Record<number, number> = {
  0: 14.62,
  5: 12.77,
  10: 11.29,
  15: 10.08,
  20: 9.09,
  25: 8.26,
  30: 7.56,
};

export function formatPercent(value: number | null): string {
  return value === null ? 'not available' : `${formatSig(value, 2)} %`;
}

/** Percent saturation; fails closed (null) for a temperature outside the reviewed table. */
export function oxygenSaturationPercent(dissolvedOxygenMgL: number, temperatureC: number): number | null {
  const saturation = OXYGEN_SATURATION_MG_L[temperatureC];
  if (saturation === undefined || !Number.isFinite(dissolvedOxygenMgL) || dissolvedOxygenMgL < 0) return null;
  return (dissolvedOxygenMgL / saturation) * 100;
}

export interface WaterSample {
  id: string;
  name: string;
  context: string;
  ph: number;
  tds: number;
  /** Given directly, or calculated from calcium and magnesium. */
  hardness?: number;
  calcium?: number;
  magnesium?: number;
  fluoride?: number;
  nitrate?: number;
  lead?: number;
  /** Electrical conductivity, µS/cm at 25 °C. Context only — no ADWG value is used. */
  conductivity: number;
  dissolvedOxygen: number;
  temperatureC: number;
  /** One sentence shown after reveal: the likely source of the result. */
  explanation: string;
}

// prettier-ignore
export const WATER_SAMPLES: WaterSample[] = [
  { id: 'town-supply', name: 'Treated town supply', context: 'Kitchen tap in a city with a fluoridated, treated surface-water supply.', ph: 7.6, tds: 180, calcium: 30, magnesium: 8, fluoride: 0.8, conductivity: 290, dissolvedOxygen: 8.6, temperatureC: 20, explanation: 'Treated surface water is usually soft to moderately hard; fluoride is added near 0.8 mg/L, well under the 1.5 mg/L health value.' },
  { id: 'inland-bore', name: 'Inland bore', context: 'Groundwater pumped from a deep bore through limestone and saline clays.', ph: 7.9, tds: 1150, calcium: 120, magnesium: 60, nitrate: 20, conductivity: 1850, dissolvedOxygen: 4.1, temperatureC: 20, explanation: 'Groundwater dissolves minerals for a long time, so it is often salty and very hard. Groundwater also tends to hold little dissolved oxygen.' },
  { id: 'rainwater-tank', name: 'Rainwater tank', context: 'Roof-collected rain stored in a plastic tank.', ph: 5.8, tds: 20, calcium: 2, magnesium: 0.5, lead: 0.002, conductivity: 30, dissolvedOxygen: 8.8, temperatureC: 20, explanation: 'Rain dissolves CO₂ from the air, forming carbonic acid, and has almost no dissolved minerals to buffer it — so it is soft and slightly acidic.' },
  { id: 'old-plumbing', name: 'First flush from old plumbing', context: 'First water drawn in the morning from a house with old brass fittings and lead-based solder.', ph: 6.7, tds: 140, hardness: 55, lead: 0.025, conductivity: 220, dissolvedOxygen: 7.9, temperatureC: 20, explanation: 'Water standing overnight in old fittings can dissolve lead. Flushing the tap before drinking reduces it; replacing fittings removes the source.' },
  { id: 'farm-bore', name: 'Shallow farm bore', context: 'Shallow bore downhill from fertilised paddocks.', ph: 7.2, tds: 450, hardness: 180, nitrate: 85, conductivity: 700, dissolvedOxygen: 6.3, temperatureC: 20, explanation: 'Nitrate from fertiliser and animal waste is very soluble and leaches into shallow groundwater.' },
  { id: 'fluoride-bore', name: 'Granite-country bore', context: 'Bore in granite rock that naturally contains fluoride minerals.', ph: 8.1, tds: 520, hardness: 150, fluoride: 2.4, conductivity: 820, dissolvedOxygen: 5.8, temperatureC: 20, explanation: 'Some rocks release fluoride naturally. Here it is above the 1.5 mg/L health value, although the water looks and tastes normal.' },
  { id: 'new-concrete-tank', name: 'New concrete tank', context: 'Rainwater stored in a newly built concrete tank.', ph: 9, tds: 300, hardness: 90, conductivity: 470, dissolvedOxygen: 8.4, temperatureC: 20, explanation: 'Fresh concrete releases calcium hydroxide, which raises the pH. The effect fades as the concrete cures.' },
  { id: 'limestone-supply', name: 'Limestone-region supply', context: 'Town water drawn from an aquifer in limestone country.', ph: 7.4, tds: 480, calcium: 64, magnesium: 18, conductivity: 760, dissolvedOxygen: 7.2, temperatureC: 25, explanation: 'Water flowing through limestone (CaCO₃) and dolomite dissolves Ca²⁺ and Mg²⁺, making it very hard. It is safe to drink but leaves scale.' },
  { id: 'desalinated', name: 'Desalinated supply', context: 'Sea water treated by reverse osmosis, then remineralised and fluoridated.', ph: 7.8, tds: 250, calcium: 20, magnesium: 2, fluoride: 0.9, conductivity: 400, dissolvedOxygen: 8.0, temperatureC: 20, explanation: 'Reverse osmosis removes almost all salts; a little calcium is added back so the water is not corrosive.' },
  { id: 'brackish-creek', name: 'Brackish creek', context: 'Creek water in a dry inland catchment affected by salinity.', ph: 8.8, tds: 2400, hardness: 620, nitrate: 12, conductivity: 3800, dissolvedOxygen: 5.2, temperatureC: 25, explanation: 'Rising saline groundwater adds salts to the creek. This water fails several aesthetic values and is not a drinking source without treatment.' },
];

export function sampleById(id: string): WaterSample | undefined {
  return WATER_SAMPLES.find((sample) => sample.id === id);
}

export function sampleHardness(sample: WaterSample): number {
  if (sample.hardness !== undefined) return sample.hardness;
  if (sample.calcium === undefined || sample.magnesium === undefined) return Number.NaN;
  return hardnessAsCaCO3(sample.calcium, sample.magnesium);
}

export function needsHardnessCalculation(sample: WaterSample): boolean {
  return sample.hardness === undefined;
}

export function sampleValue(sample: WaterSample, parameter: QualityParameter): number | undefined {
  if (parameter === 'hardness') return sampleHardness(sample);
  return sample[parameter];
}

export function measuredParameters(sample: WaterSample): QualityParameter[] {
  return QUALITY_PARAMETER_ORDER.filter((parameter) => sampleValue(sample, parameter) !== undefined);
}

export function exceedsGuideline(parameter: QualityParameter, value: number): boolean {
  const guideline = QUALITY_GUIDELINES[parameter];
  if (guideline.min !== undefined && value < guideline.min) return true;
  return value > guideline.max;
}

export function failingParameters(sample: WaterSample): QualityParameter[] {
  return measuredParameters(sample).filter((parameter) => {
    const value = sampleValue(sample, parameter);
    return value !== undefined && exceedsGuideline(parameter, value);
  });
}

export function formatGuideline(parameter: QualityParameter): string {
  const guideline = QUALITY_GUIDELINES[parameter];
  const unit = guideline.unit ? ` ${guideline.unit}` : '';
  return guideline.min !== undefined
    ? `${guideline.min}–${guideline.max}${unit}`
    : `≤ ${guideline.max}${unit}`;
}

export function formatParameterValue(parameter: QualityParameter, value: number): string {
  const unit = QUALITY_GUIDELINES[parameter].unit;
  return `${formatSig(value, 3)}${unit ? ` ${unit}` : ''}`;
}

export function checkHardnessAnswer(sample: WaterSample, answer: number): AnswerCheck | null {
  if (sample.calcium === undefined || sample.magnesium === undefined) return null;
  const expected = hardnessAsCaCO3(sample.calcium, sample.magnesium);
  return checkNumericAnswer(
    answer,
    expected,
    [
      { value: sample.calcium + sample.magnesium, feedback: 'Adding the Ca²⁺ and Mg²⁺ masses is not enough. Hardness is reported as the mass of CaCO₃ that would hold the same number of moles, so multiply Ca by 2.497 and Mg by 4.118.' },
      { value: sample.calcium * CA_TO_CACO3, feedback: 'You converted only the calcium. Magnesium ions also cause hardness: add 4.118 × [Mg²⁺].' },
      { value: (sample.calcium + sample.magnesium) * CA_TO_CACO3, feedback: 'Magnesium has a smaller molar mass than calcium, so each mg of Mg²⁺ counts for more CaCO₃: use 100.09 ÷ 24.31 = 4.118 for magnesium.' },
    ],
    `Hardness = ${formatSig(expected, 3)} mg/L as CaCO₃ — ${hardnessClass(expected)} water.`,
    'mg/L',
  );
}

export function hardnessWorking(sample: WaterSample): string[] {
  if (sample.calcium === undefined || sample.magnesium === undefined) return [];
  return [
    `Ca factor = M(CaCO₃) ÷ M(Ca) = 100.09 ÷ 40.08 = ${formatSig(CA_TO_CACO3, 4)}`,
    `Mg factor = M(CaCO₃) ÷ M(Mg) = 100.09 ÷ 24.31 = ${formatSig(MG_TO_CACO3, 4)}`,
    `Hardness = ${formatSig(CA_TO_CACO3, 4)} × ${sample.calcium} + ${formatSig(MG_TO_CACO3, 4)} × ${sample.magnesium} = ${formatSig(hardnessAsCaCO3(sample.calcium, sample.magnesium), 3)} mg/L as CaCO₃`,
  ];
}

export interface SampleCheckResult {
  correct: boolean;
  missed: QualityParameter[];
  wronglyFlagged: QualityParameter[];
  failing: QualityParameter[];
  feedback: string[];
}

/** Fails closed (null) when the selection names a parameter that was not measured. */
export function checkSampleFlags(
  sample: WaterSample,
  flagged: QualityParameter[],
): SampleCheckResult | null {
  const measured = measuredParameters(sample);
  if (flagged.some((parameter) => !measured.includes(parameter))) return null;
  const failing = failingParameters(sample);
  const missed = failing.filter((parameter) => !flagged.includes(parameter));
  const wronglyFlagged = flagged.filter((parameter) => !failing.includes(parameter));
  const feedback: string[] = [];
  for (const parameter of missed) {
    const guideline = QUALITY_GUIDELINES[parameter];
    const value = sampleValue(sample, parameter) ?? Number.NaN;
    feedback.push(
      `Missed: ${guideline.label} is ${formatParameterValue(parameter, value)}, outside the ${guideline.kind} value ${formatGuideline(parameter)}. ${guideline.reason}`,
    );
  }
  for (const parameter of wronglyFlagged) {
    const guideline = QUALITY_GUIDELINES[parameter];
    const value = sampleValue(sample, parameter) ?? Number.NaN;
    feedback.push(
      `${guideline.label} is ${formatParameterValue(parameter, value)}, which meets the guideline (${formatGuideline(parameter)}), so it should not be flagged.`,
    );
  }
  const correct = missed.length === 0 && wronglyFlagged.length === 0;
  if (correct) {
    feedback.push(
      failing.length === 0
        ? 'Every measured value meets its ADWG guideline value.'
        : `You found every value outside its guideline: ${failing.map((parameter) => QUALITY_GUIDELINES[parameter].label).join(', ')}.`,
    );
  }
  if (failing.some((parameter) => QUALITY_GUIDELINES[parameter].kind === 'health')) {
    feedback.push('A health guideline value is exceeded, so this water should not be drunk until the cause is fixed.');
  } else if (failing.length > 0) {
    feedback.push('Only aesthetic values are exceeded: taste, scale or corrosion problems rather than a direct health risk.');
  }
  return { correct, missed, wronglyFlagged, failing, feedback };
}

export const WATER_QUALITY_BOUNDARIES: string[] = [
  'Samples are illustrative teaching cases, not results from a real water supply.',
  'Only the listed ADWG values are used. The full guidelines cover many more parameters and require sampling programs, not a single result.',
  'Conductivity and dissolved oxygen are shown as context; this tool does not compare them with a guideline value.',
  'Oxygen saturation uses the reviewed solubility table at 0–30 °C in 5 °C steps. Other temperatures are not interpolated or extrapolated.',
  'This page is not a water-testing service. Ask your water supplier or local health authority about your own water.',
];
